import React, { useMemo, useEffect } from 'react';
import Sessions from 'features/activity/pages/Sessions';
import axiosInstance from 'shared/services/api/axiosInstance';

const ActivityPage = ({ onMenuClick, screenSize, selectedPage }) => {
  // Match Sessions.js: activity APIs are team-scoped; site id (idy) is not required to list sessions.
  const teamId = useMemo(() => {
    try {
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return selectedTeamData._id || localStorage.getItem('selectedTeam') || '';
    } catch (e) {
      return localStorage.getItem('selectedTeam') || '';
    }
  }, []);

  // Best-effort: set idy when missing so website pickers / analytics stay aligned (non-blocking).
  useEffect(() => {
    if (!teamId || localStorage.getItem('idy')) return;
    let cancelled = false;
    (async () => {
      try {
        const key = teamId != null ? String(teamId).trim() : '';
        const is24CharHexTeamKey = /^[a-fA-F0-9]{24}$/.test(key);
        // Postgres team.id (e.g. 87) must use /website/team/:id — /website/name/:x expects a team name string.
        const isNumericTeamId = /^\d+$/.test(key);
        let response;
        if (is24CharHexTeamKey) {
          try {
            response = await axiosInstance.get(`/website/team/${encodeURIComponent(key)}`);
          } catch {
            const teamName = localStorage.getItem('selectedTeam');
            response = await axiosInstance.get(
              `/website/name/${encodeURIComponent(teamName || key)}`
            );
          }
        } else if (isNumericTeamId) {
          response = await axiosInstance.get(`/website/team/${encodeURIComponent(key)}`);
        } else {
          const teamName = localStorage.getItem('selectedTeam') || key;
          response = await axiosInstance.get(`/website/name/${encodeURIComponent(teamName)}`);
        }
        if (cancelled || response.status !== 200 || !response.data?.websites?.length) return;
        const raw = response.data.websites;
        const active = raw.filter((w) => w.is_verified || w.isVerified);
        const list = active.length > 0 ? active : raw;
        const first = list[0];
        const id = first.site_id || first.siteId;
        if (id && !localStorage.getItem('idy')) {
          localStorage.setItem('idy', id);
          const domain = first.domain || first.Domain || '';
          if (domain) localStorage.setItem('selectedWebsite', domain);
        }
      } catch {
        // Ignore: Sessions still loads via teamId
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  // Pass-through wrapper. Older versions wrapped Sessions in h-screen /
  // overflow-hidden containers that constrained the page to the viewport and
  // prevented Dashboard's outer scroll from picking up the table. Events
  // doesn't go through this file (it's rendered directly in Dashboard) and
  // that's why Events was scrolling correctly while Sessions wasn't.
  if (!teamId) {
    return (
      <div className="bg-surface p-6">
        <div className="rounded-lg border border-[color:var(--border-default)] bg-surface p-6 shadow-[0_1px_0_0_rgba(26,26,25,0.04)]">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--trodo-primary)]">
                <svg className="h-5 w-5 text-ink-onBrand" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-[color:var(--text-primary)]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Select a workspace to view activity.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Sessions
      onMenuClick={onMenuClick}
      screenSize={screenSize}
      selectedPage={selectedPage}
    />
  );
};

export default ActivityPage;
